/**
 * AIVA-6685 (V-08 — no sensitive data in browser localStorage): integration
 * modules keep their UI preferences and caches (selected site / location,
 * tracked jobs, cached titles, …) in `sessionStorage` rather than `localStorage`,
 * so nothing an add-in feature persists is left in a store a same-origin XSS can
 * read across sessions.
 *
 * This is safe for these values because they are read and written only by the
 * taskpane (one window). Cross-window pane→dialog bridges — the MSAL dialog
 * cache and the create-slide theme bridge (`themeColors.ts`), and the
 * command-palette recents/favorites shared with the All-Actions dialog — must
 * stay in `localStorage` (a separate dialog window can't see per-window
 * sessionStorage) and are intentionally NOT migrated by this helper.
 */

/**
 * Read a preference from `sessionStorage`, migrating a legacy `localStorage`
 * value once: if the key is absent from sessionStorage but present in
 * localStorage (a pre-upgrade session), the value is copied into sessionStorage
 * and the localStorage original is removed. Returns the resolved value, or null
 * when neither store has it (or storage is unavailable).
 */
export function readMigratedSessionPref(key: string): string | null {
  try {
    const current = globalThis.sessionStorage?.getItem(key) ?? null;
    if (current !== null) return current;
    const legacy = globalThis.localStorage?.getItem(key) ?? null;
    if (legacy !== null) {
      globalThis.sessionStorage?.setItem(key, legacy);
      globalThis.localStorage?.removeItem(key);
    }
    return legacy;
  } catch {
    return null;
  }
}
